import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const configuredContactEmail = (process.env.REACT_APP_CONTACT_EMAIL || "").trim();
let cachedContactEmail = configuredContactEmail;
let contactEmailRequest = null;

export const getContactEmailLabel = (email) =>
  email || "Contact email unavailable";

export const createEmailComposeUrl = ({
  email = cachedContactEmail,
  subject,
} = {}) => {
  const contactEmail = String(email || "").trim();
  if (!contactEmail) return undefined;

  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: contactEmail,
  });

  if (subject) params.set("su", subject);

  return `https://mail.google.com/mail/?${params.toString()}`;
};

export function useContactEmail() {
  const [contactEmailState, setContactEmailState] = useState({
    email: cachedContactEmail,
    isLoaded: Boolean(cachedContactEmail),
  });

  useEffect(() => {
    if (cachedContactEmail) {
      setContactEmailState({
        email: cachedContactEmail,
        isLoaded: true,
      });
      return undefined;
    }

    let ignore = false;
    if (!contactEmailRequest) {
      contactEmailRequest = fetch("/api/public-config")
        .then((response) => (response.ok ? response.json() : {}))
        .then((config) => String(config.contactEmail || "").trim())
        .then((email) => {
          cachedContactEmail = email;
          return email;
        })
        .catch(() => "");
    }

    contactEmailRequest.then((email) => {
      if (!ignore) {
        setContactEmailState({
          email,
          isLoaded: true,
        });
      }
    });

    return () => {
      ignore = true;
    };
  }, []);

  return {
    contactEmail: contactEmailState.email,
    isContactEmailLoaded: contactEmailState.isLoaded,
  };
}

export function NexusPublicNav({ onPricingClick, onStoreClick }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const scrollToTop = () => {
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 80);
  };
  const handlePageLinkClick = () => {
    closeMobileMenu();
    scrollToTop();
  };
  const navItemClass = (paths) => {
    const pathList = Array.isArray(paths) ? paths : [paths];
    return `nexus-nav__item ${
      pathList.includes(location.pathname) ? "is-active" : ""
    }`;
  };
  const isStoreActive =
    isHome && new URLSearchParams(location.search).get("section") === "store";

  const moveToLandingSection = (section) => {
    closeMobileMenu();

    if (isHome) {
      if (section === "pricing") onPricingClick?.();
      if (section === "store") onStoreClick?.();
      return;
    }

    navigate(`/?section=${section}`, { state: { section } });
  };

  return (
    <nav className="nexus-nav" aria-label="Public navigation">
      <Link className="nexus-nav__brand" to="/">
        <span className="nexus-nav__mark" aria-hidden="true">
          N
        </span>
        <span>Nexus Game Engine</span>
      </Link>
      <div className="nexus-nav__links">
        <Link
          className={`nexus-nav__item ${isHome && !isStoreActive ? "is-active" : ""}`}
          to="/"
          onClick={handlePageLinkClick}
        >
          Home
        </Link>
        <Link className={navItemClass("/mobile")} to="/mobile" onClick={handlePageLinkClick}>
          Mobile
        </Link>
        <Link
          className={navItemClass(["/sales", "/pricing", "/faq"])}
          to="/sales"
          onClick={handlePageLinkClick}
        >
          Sales
        </Link>
        <button
          className={`nexus-nav__item nexus-nav__link-button ${isStoreActive ? "is-active" : ""}`}
          type="button"
          onClick={() => moveToLandingSection("store")}
        >
          Store
        </button>
        <Link className={navItemClass("/login")} to="/login" onClick={handlePageLinkClick}>
          Login
        </Link>
        <button
          className="nexus-nav__cta"
          type="button"
          onClick={() => moveToLandingSection("pricing")}
        >
          Launch Your Club
        </button>
      </div>
      <button
        className="nexus-nav__menu-button"
        type="button"
        onClick={() => setMobileMenuOpen((open) => !open)}
        aria-expanded={mobileMenuOpen}
        aria-controls="nexus-mobile-menu"
        aria-label="Toggle navigation menu"
      >
        {mobileMenuOpen ? (
          <X size={22} aria-hidden="true" />
        ) : (
          <Menu size={22} aria-hidden="true" />
        )}
      </button>
      <div
        id="nexus-mobile-menu"
        className={`nexus-nav__mobile-menu ${mobileMenuOpen ? "is-open" : ""}`}
      >
        <Link
          className={`nexus-nav__item ${isHome && !isStoreActive ? "is-active" : ""}`}
          to="/"
          onClick={handlePageLinkClick}
        >
          Home
        </Link>
        <Link className={navItemClass("/mobile")} to="/mobile" onClick={handlePageLinkClick}>
          Mobile
        </Link>
        <Link
          className={navItemClass(["/sales", "/pricing", "/faq"])}
          to="/sales"
          onClick={handlePageLinkClick}
        >
          Sales
        </Link>
        <button
          className={`nexus-nav__item nexus-nav__mobile-link-button ${isStoreActive ? "is-active" : ""}`}
          type="button"
          onClick={() => moveToLandingSection("store")}
        >
          Store
        </button>
        <Link className={navItemClass("/login")} to="/login" onClick={handlePageLinkClick}>
          Login
        </Link>
        <button
          className="nexus-nav__mobile-cta"
          type="button"
          onClick={() => moveToLandingSection("pricing")}
        >
          Launch Your Club
        </button>
      </div>
    </nav>
  );
}

export function NexusFooter() {
  return (
    <footer className="nexus-footer">
      <div>
        <span className="nexus-footer__brand">Nexus Game Engine</span>
        <p>
          Nexus Game Engine is a productivity suite for private membership
          organizations. We provide administrative software and do not process
          member funds.
        </p>
      </div>
      <nav aria-label="Legal links">
        <Link to="/terms" target="_blank" rel="noopener noreferrer">
          Terms of Service
        </Link>
        <Link to="/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </Link>
        <Link to="/acceptable-use" target="_blank" rel="noopener noreferrer">
          Acceptable Use Policy
        </Link>
      </nav>
    </footer>
  );
}
